import React from 'react'
import styled from '@emotion/styled'
import { Link } from 'gatsby'
import { trackCustomEvent } from 'gatsby-plugin-google-analytics'
import CustomImage from './custom-image'
import { getHref } from '../utils'

const getOptimizedImage = (imageUrl, image21x9Url, poster) => {
    if (imageUrl) {
        return imageUrl.replace(/\/original\.([a-z]+)$/g, '/360x200.$1')
    }

    if (image21x9Url || poster) {
        // fallback to 21x9 image
        const url = image21x9Url || poster
        return url.replace(/\/original\.([a-z]+)$/g, '/360x154.$1')
    }

    // fallback to 16x9 placeholder image
    return '/video_thumbnail_placeholder.png'
}

const getCardText = (ctitle, description, useEditorialFields = false, marketingTitle) => {
    if (useEditorialFields && marketingTitle && marketingTitle.length > 0) {
        return marketingTitle
    }

    return ctitle || description
}

const getCustomerFacingType = edifloType => {
    let customerFacingType = ''

    switch (edifloType) {
        case 'LibrarySeries':
            customerFacingType = 'Series'
            break
        case 'LibraryEvent':
            customerFacingType = 'Asset'
            break
        case 'channel-collection':
            customerFacingType = 'Collection'
            break
        case 'channel-stream':
            customerFacingType = 'Collection'
            break
        case 'LibraryCollection':
            customerFacingType = 'Collection'
            break
        default:
            customerFacingType = '(Type not recognized)'
            break
    }

    return customerFacingType
}

const sendGaCustomEvent = (e, title, edifloId, type) => {
    const label = `${title || edifloId || '(not set)'} - ${getCustomerFacingType(type)}`

    trackCustomEvent({
        category: 'Navigation',
        action: 'Select Content',
        label,
    })
}

const Card = props => {
    const {
        ctitle,
        description,
        id,
        image,
        image21x9,
        marketingTitle,
        poster,
        type,
        useEditorialFields,
        useGridStyling,
    } = props

    const optimizedImg = getOptimizedImage(image, image21x9, poster)

    const href = getHref(props)
    const cardText = getCardText(ctitle, description, useEditorialFields, marketingTitle)

    return (
        <CustomCard className={`custom-card ${useGridStyling && 'grid-styling'}`} key={`cc-${id}`}>
            <Link
                to={href}
                state={props}
                onClick={ev => {
                    sendGaCustomEvent(ev, ctitle, id, type)
                }}
            >
                <div className="card">
                    <CustomImage imgSrc={optimizedImg} alt={ctitle} />
                </div>
                {cardText ? <p className="description">{cardText}</p> : null}
            </Link>
        </CustomCard>
    )
}

const CustomCard = styled.div`
    display: inline-block;
    width: 210px;
    padding: 0 8px;
    transition: 0.3s ease-in-out;

    a {
        text-decoration: none;
    }

    .card {
        display: flex;
        width: 210px;
        height: 118px;
        align-items: center;
        justify-content: center;
        position: relative;
        margin-bottom: 0.375rem;
        background-size: cover;

        img {
            position: absolute;
            width: 210px;
            height: 118px;
            top: 0;
            box-shadow: 0px 6px 16px 0px rgba(0, 0, 0, 0.5);
        }
    }

    .description {
        display: -webkit-box;
        -webkit-box-orient: vertical;
        -webkit-line-clamp: 2; /* max number of lines to show */
        opacity: 0.7;
        width: 100%;
        color: rgb(255, 255, 255);
        font-size: 16px;
        font-family: 'Source Sans Pro', sans-serif;
        font-weight: normal;
        letter-spacing: 0.09px;
        line-height: 20px;
        white-space: pre-line;
        overflow: hidden;
        text-overflow: ellipsis;
        margin: 0;
    }

    &:hover {
        cursor: pointer;
        transition: 0.3s ease-in-out;
        transform: scale(1.05);

        .description {
            opacity: 1;
        }
    }

    &.grid-styling {
        width: unset;
        padding-bottom: 36px;

        .card {
            display: block;
            width: unset;
            height: unset;
            align-items: unset;
            justify-content: unset;

            .card-img {
                width: 100%;
                height: 100%;

                .add-padding {
                    width: 100%;
                    padding-bottom: 56.25%;
                }

                img {
                    object-fit: cover;
                    width: 100%;
                    height: 100%;
                }
            }
        }
    }

    @media screen and (max-width: 672px) {
        padding-right: 0;

        .description {
            font-size: 15px;
            letter-spacing: 0.08px;
        }

        &.grid-styling {
            padding-left: 0;
            padding-bottom: 24px;
        }
    }
`

export default Card
